import React, { Suspense, useEffect, useState } from "react";
import { LanguageChange, LoadingState, SideOverlay } from "../../components";
import { IconButton, Snackbar, Stack, Typography, useTheme } from "@mui/material";
import { ProfileMenu } from "../../pages/update-profile/profile-menu";
import Projects from "../../pages/Projects";
import { useStore } from "../../helpers";
import { safe_get } from "../../report-visuals";
import { observer } from "mobx-react-lite";
import { useLocation, useNavigate } from "react-router";
import { LogoutModal } from "../../components/logout-modal";
import { IconArrowsShuffle, IconMenu3 } from "@tabler/icons-react";
import NavItemsDropdown from "./nav-items-dropdown";
import { MobileNavBar } from "../MobileNavBar";
import { Vortex } from "react-loader-spinner";
import { LanguageIcon } from "@heroicons/react/24/solid";

const NewNavBar = observer(() => {
    const rootStore = useStore();
    const theme = useTheme();
    const { pathname } = useLocation();
    const path = pathname.split("/")[1];
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const handleProjectSwitch = () => rootStore.update_switch_project(false);
    const handleLogoutConfirm = () => {
        const device_id = localStorage.getItem("device_id");
        localStorage.clear();
        sessionStorage.clear();
        localStorage.setItem("device_id", device_id);
        navigate("/login", { replace: true });
        rootStore.update_logoutModal(false);
    };

    const project_name = safe_get(rootStore.projectStore.activeProject, "name", "");
    const appMode = rootStore.appMode;

    const projectTimestamp =
        safe_get(rootStore.projectStore.activeProject, "timestamp", "") ||
        safe_get(rootStore.projectStore.activeProject, "date", "");

    if (appMode) return null;

    return (
        <Suspense fallback={<LoadingState />}>
            <div className="sticky top-0 z-30 w-full border-b border-gray-200 bg-white/80 shadow-sm backdrop-blur-md">
                <div className="flex items-center justify-between px-2 py-3 md:h-16 md:px-4">
                    <div className="relative flex min-w-0 items-center gap-3">
                        <NavItemsDropdown />

                        <div className="flex min-w-0 items-center gap-2">
                            <div
                                className="flex min-w-0 cursor-pointer flex-col"
                                onClick={() => {
                                    setOpen(true);
                                    setTimeout(() => {
                                        rootStore.update_switch_project(true);
                                    }, 700);
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 700, fontSize: "1.15rem", lineHeight: 1.2 }}
                                    className="truncate tracking-wide text-gray-800"
                                    title={
                                        path === "project"
                                            ? project_name || "Loading Project..."
                                            : path.replace(/[-_]/g, " ").toUpperCase()
                                    }
                                >
                                    {path === "project" ? (
                                        project_name ? (
                                            project_name
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <Vortex
                                                    height="25"
                                                    width="25"
                                                    ariaLabel="vortex-loading"
                                                    wrapperClass="vortex-wrapper"
                                                    colors={[
                                                        "red",
                                                        "green",
                                                        "blue",
                                                        "yellow",
                                                        "orange",
                                                        "purple",
                                                    ]}
                                                />
                                                <span>Loading Project...</span>
                                            </div>
                                        )
                                    ) : (
                                        path.replace(/[-_]/g, " ").toUpperCase()
                                    )}
                                </Typography>
                            </div>

                            {path === "project" && (
                                <IconButton
                                    size="small"
                                    disableRipple
                                    onClick={() => rootStore.update_switch_project(true)}
                                    sx={{
                                        padding: 0.5,
                                        color: "#888888",
                                        "&:hover": {
                                            backgroundColor: "#f3f4f6",
                                        },
                                    }}
                                >
                                    <IconArrowsShuffle size={18} />
                                </IconButton>
                            )}
                        </div>
                        <button
                            className="btn btn-sm gap-2"
                            onClick={() => {
                                rootStore.update_language_Change(true);
                            }}
                        >
                            <LanguageIcon className="h-4 w-4" />
                        </button>
                        <LanguageChange />
                    </div>

                    <Snackbar
                        open={open}
                        autoHideDuration={3000}
                        onClose={() => setOpen(false)}
                        message={project_name}
                    />

                    <div className="flex items-center md:pr-4">
                        <div className="mr-2">
                            {path === "project" && projectTimestamp && (
                                <div className="mt-1 inline-flex w-fit items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600">
                                    <span className="font-medium text-slate-500">Issued</span>
                                    <span>•</span>
                                    <span>{projectTimestamp}</span>
                                </div>
                            )}
                        </div>
                        <ProfileMenu />
                        <IconButton
                            onClick={() => rootStore.update_appDrawerToggle(true)}
                            sx={{
                                display: {
                                    xs: "flex",
                                    md: "none",
                                },
                                color: "#1b1b1d",
                            }}
                        >
                            <IconMenu3 />
                        </IconButton>
                    </div>
                </div>
            </div>
            <MobileNavBar />
            <SideOverlay
                onClose={handleProjectSwitch}
                show={rootStore.switch_project}
                title="Projects"
                size="small"
            >
                <Projects />
            </SideOverlay>

            <LogoutModal
                isOpen={rootStore.logoutModal}
                desc="Are you sure you want to logout?"
                onDeleteClick={handleLogoutConfirm}
                onCancelClick={() => rootStore.update_logoutModal(false)}
            />
        </Suspense>
    );
});

export default NewNavBar;
